import Button from "@/common/elements/Button";
import DataTable from "@/common/elements/DataTable";
import { StyledFlex, StyledSectionHeading, StyledSubTableContainer } from "@/common/styles";
import CustomModal from "@/components/CustomModal";
import { useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import ServiceAttemptDetailForm from "./ServiceAttemptDetailForm";
import useActiveWarrant from "@/hooks/useActiveWarrant";
import useDialog from "@/hooks/useDialog";
import notificationService from "@/util/notificationService";
import { patchActiveWarrant } from "@/slices/warrantsSlice";
import { getOptionsFromRefList } from "@/util/common_function";
import dayjs from "dayjs";
import { Popconfirm } from "antd";
import useGridColumnConfig from "@/hooks/useGridColumnConfig";

const formatTime = (t) => {
  if (!t) return "";
  if (Array.isArray(t))
    return `${String(t[0]).padStart(2,"0")}:${String(t[1]).padStart(2,"0")}`;
  return t; // already a string
};

const SERVICE_ATTEMPTS_HEADERS = [
  { key: "attemptDate",    text: "Attempt Date"    },
  { key: "attemptTime",    text: "Attempt Time"    },
  { key: "servingOfficer", text: "Serving Officer" },
  // { key: "serviceType",    text: "Service Type"    },
  { key: "serviceStatus",  text: "Successfull"  },
  { key: "servedTo",       text: "Served at"       },
];

const ServiceAttemptDetails = ({ warrantForm }) => {
  const dispatch = useDispatch();
  const { activeWarrant, bookingsFieldsEnabled, patchArray } = useActiveWarrant();
  const { bookings } = useSelector((s) => s?.bookings);
  const { showDialog } = useDialog();
  const formRef = useRef(null);

  const [showModal, setShowModal] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [prefilledAddress, setPrefilledAddress] = useState(null);

  const serviceDocuments = activeWarrant?.serviceDocuments ?? [];

  // "Config" — drag-reorder/resize columns, persisted per-user (see
  // docs/plans/grid-column-config-feature.md). This tab is only reachable
  // from the "Warrants New" module (single-warrant detail view), so the
  // gridId is hardcoded the same way Bookings' grids are.
  const {
    headers: tableHeaders,
    isConfiguring,
    configButtonLabel,
    startConfiguring,
    confirmSave,
    discardChanges,
    dataTableRef,
  } = useGridColumnConfig("Warrants New:ServiceAttempts", SERVICE_ATTEMPTS_HEADERS);

  const tableRows = serviceDocuments.flatMap((doc, docIdx) =>
    (doc.serviceTypes ?? []).map((st, stIdx) => ({
      _docIdx:        docIdx,
      _stIdx:         stIdx,
      attemptDate:    st.dateServed    ?? "",
      attemptTime:    formatTime(st.timeServed),
      servingOfficer: st.servedByStaffID ? String(st.servedByStaffID) : "",
      // serviceType:    st.serviceType   ?? "",
      serviceStatus:  st.serviceStatus ?? "",
      servedTo:       st.servedTo      ?? "",
    }))
  );

  const resolveStateSubmissionAddress = () => {
    const targetId = bookings?.addressToSendToStateID;
    if (!targetId) return null;

    const bioAddresses = bookings?.bio?.[0]?.bioAddresses ?? [];

    const match = bioAddresses.find(
      (b) =>
        b?.bioAddressId === targetId ||
        b?.uuid === targetId ||
        b?.addresses?.[0]?.addressId === targetId
    );

    return match?.addresses?.[0] ?? null;
  };

  const openAdd = () => {
    setSelectedIndex(null);
    setPrefilledAddress(resolveStateSubmissionAddress());
    setShowModal(true);
  };

  const openDetails = () => {
    if (selectedIndex === null) {
      notificationService.error("Please select a service attempt first");
      return;
    }
    setPrefilledAddress(null); // editing existing record — keep its saved address
    setShowModal(true);
  };

  const handleRowClick = (row) => {
    setSelectedIndex({ docIdx: row._docIdx, stIdx: row._stIdx });
  };

  const handleModalSave = () => {
    formRef.current?.submit();
  };

  const handleSave = (formData) => {
    const updated = [...serviceDocuments];

    if (selectedIndex === null) {
      // Add — new serviceDocument with one serviceType
      updated.push({
        serviceDocumentID:        0,
        warrantID:                activeWarrant?.warrantId ?? activeWarrant?.warrantID ?? 0,
        civilPaperDefendantID:    null,
        businessID:               null,
        dateCompleted:            formData.dateServed  ?? null,
        timeCompleted:            formData.timeServed  ?? null,
        completionStatus:         formData.serviceStatus ?? null,
        comments:                 formData.docComments ?? null,
        personToBeServedJacketID: null,
        initiationDate:           null,
        serviceTypes: [buildServiceType(formData)],
      });
    } else {
      const { docIdx, stIdx } = selectedIndex;
      const doc   = { ...updated[docIdx] };
      const types = [...(doc.serviceTypes ?? [])];
      types[stIdx] = { ...types[stIdx], ...buildServiceType(formData) };
      doc.serviceTypes = types;
      updated[docIdx] = doc;
    }

    patchArray("serviceDocuments", updated);

    // ─── Auto-clear when "Successful" is checked ──────────────────────────
    if (formData.successful === true) {
      const statusOptions      = getOptionsFromRefList("Status", false, false, true);
      const clearedOption      = statusOptions.find((o) => o.label?.toUpperCase() === "CLEARED");

      const clearanceReasonOptions = getOptionsFromRefList("Warrant_Clearance_Reason", false, false, true);
      const arrestedReason         = clearanceReasonOptions.find((o) => o.label?.toUpperCase() === "ARRESTED");

      const userDetails = JSON.parse(localStorage.getItem("userDetails") || "{}");
      const staffId     = userDetails?.staffId || userDetails?.staffID || null;

      const now           = dayjs();
      const clearanceDate = now.format("MM/DD/YYYY");
      const clearanceTime = now.format("HH:mm");

      // 1. Patch statusId + clearanceInfo into warrantsSlice
      dispatch(patchActiveWarrant({
        statusId: clearedOption?.value ?? activeWarrant?.statusId,
        warrantClearanceInfo: [{
          ...(activeWarrant?.warrantClearanceInfo?.[0] ?? {}),
          clearanceReasonId: arrestedReason?.value ?? null,
          clearanceDate,
          clearanceTime,
          clearedByStaffId: staffId ? Number(staffId) : null,
        }],
      }));

      // 2. Sync the WarrantInformations form so UI reflects changes immediately
      warrantForm?.setFieldsValue({
        statusId:          clearedOption?.value ?? null,
        clearanceReasonId: arrestedReason?.value ?? null,
        clearanceDateTime: now,
        clearedByStaffId:  staffId ? Number(staffId) : null,
      });
    }
    // ─────────────────────────────────────────────────────────────────────

    setShowModal(false);
    setSelectedIndex(null);
  };

  const handleDelete = async () => {
    if (selectedIndex === null) {
      notificationService.error("Please select a service attempt to delete");
      return;
    }

    const canDelete = await new Promise((resolve) =>
      showDialog("confirm", {
        title: "Warning",
        content: "Are you sure you want to delete this service attempt?",
        onConfirm: () => resolve(true),
        onCancel:  () => resolve(false),
      })
    );
    if (!canDelete) return;

    const { docIdx, stIdx } = selectedIndex;
    const updated = [...serviceDocuments];
    const doc     = { ...updated[docIdx] };
    const types   = doc.serviceTypes.filter((_, i) => i !== stIdx);

    if (types.length === 0) {
      updated.splice(docIdx, 1);
    } else {
      doc.serviceTypes = types;
      updated[docIdx]  = doc;
    }

    patchArray("serviceDocuments", updated);
    setSelectedIndex(null);
  };

  const selectedServiceType =
    selectedIndex !== null
      ? serviceDocuments[selectedIndex.docIdx]?.serviceTypes?.[selectedIndex.stIdx] ?? null
      : null;

  const selectedServiceDoc =
    selectedIndex !== null ? serviceDocuments[selectedIndex.docIdx] ?? null : null;

  return (
    <>
      <StyledSectionHeading marginTop="20px">
        <span>Service Attempts</span>
      </StyledSectionHeading>

      <StyledSubTableContainer style={{ marginBottom: "25px" }}>
        <StyledFlex justify="space-between" align="center" marginBottom="5px">
          <h2 style={{ margin: "0" }}>List of Service Attempts</h2>
          <StyledFlex gap="10px">
            <Button onClick={openAdd}        disabled={!bookingsFieldsEnabled || isConfiguring}>Add</Button>
            <Button onClick={openDetails}    disabled={selectedIndex === null || isConfiguring}>Details</Button>
            <Button onClick={handleDelete}   disabled={selectedIndex === null || !bookingsFieldsEnabled || isConfiguring}>Delete</Button>

            {/* Config — drag-reorder/resize columns */}
            {!isConfiguring ? (
              <Button htmlType="button" onClick={startConfiguring}>
                {configButtonLabel}
              </Button>
            ) : (
              <Popconfirm
                title="Save the current column layout?"
                onConfirm={confirmSave}
                onCancel={discardChanges}
                okText="Yes"
                cancelText="No"
              >
                <Button htmlType="button">{configButtonLabel}</Button>
              </Popconfirm>
            )}
          </StyledFlex>
        </StyledFlex>

        <DataTable
          ref={dataTableRef}
          headers={tableHeaders}
          draggable={isConfiguring}
          data={tableRows}
          rowUniqueId="_stIdx"
          onRowClick={handleRowClick}
        />
      </StyledSubTableContainer>

      {showModal && (
        <CustomModal
          show={showModal}
          onHide={() => { setShowModal(false); setSelectedIndex(null); }}
          header="Service Attempt Details"
          size="xl"
          hideFooter={!bookingsFieldsEnabled}
          onSaveClick={handleModalSave}
          onCloseClick={() => { setShowModal(false); setSelectedIndex(null); }}
          submitButtonText="Save"
          closeButtonText="Cancel"
        >
          <ServiceAttemptDetailForm
            ref={formRef}
            serviceType={selectedServiceType}
            serviceDoc={selectedServiceDoc}
            isReadOnly={!bookingsFieldsEnabled}
            prefilledAddress={prefilledAddress}
            onSave={handleSave}
          />
        </CustomModal>
      )}
    </>
  );
};

function buildServiceType(f) {
  return {
    serviceTypeID:        f.serviceTypeID        ?? 0,
    serviceDocumentID:    f.serviceDocumentID    ?? 0,
    serviceType:          f.serviceType          ?? null,
    serviceInstructions:  f.serviceInstructions  ?? null,
    deputizedAgencyName:  f.deputizedAgencyName  ?? null,
    serviceStatus:        f.serviceStatus        ?? null,
    servedByStaffID:      f.servedByStaffID      ?? null,
    personServedJacketID: f.personServedJacketID ?? null,
    dateServed:           f.dateServed           ?? null,
    timeServed:           f.timeServed           ?? null,
    comments:             f.comments             ?? null,
    servedTo:             f.servedTo             ?? null,
    dateAdded:            f.dateAdded            ?? null,
    timeAdded:            f.timeAdded            ?? null,
    actualAddressID:      f.actualAddressID      ?? null,
    assignedToStaffID:    f.assignedToStaffID    ?? null,
    serviceArea:          f.serviceArea          ?? null,
    successful:           f.successful           ?? false,
  };
}

export default ServiceAttemptDetails;